// src/pages/Contact.js
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Card,
  CardContent,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getFirestore, addDoc, collection, serverTimestamp } from "firebase/firestore";

import { Helmet } from "react-helmet-async";

const COMPANY_SIZES = [
  "1–10",
  "11–50",
  "51–200",
  "201–500",
  "501–1,000",
  "1,001–5,000",
  "5,001–10,000",
  "10,000+",
];

const COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "United Arab Emirates",
  "Canada",
  "Australia",
  "Singapore",
  "Germany",
  "France",
  "Other",
];

const initialForm = {
  firstName: "",
  lastName: "",
  companyEmail: "",
  phone: "",
  companyName: "",
  companySize: "",
  country: "",
  discussion: "",
  consent: false,
};

export default function Contact() {
  const db = useMemo(() => getFirestore(), []);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (field) => (e) => {
    const value = field === "consent" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((err) => ({ ...err, [field]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "First name is required";
    if (!form.lastName.trim()) e.lastName = "Last name is required";
    if (!form.companyEmail.trim()) e.companyEmail = "Company email is required";
    if (form.companyEmail && !/^\S+@\S+\.\S+$/.test(form.companyEmail))
      e.companyEmail = "Enter a valid email";
    if (!form.companySize) e.companySize = "Select a company size";
    if (!form.country) e.country = "Select a country";
    if (!form.discussion.trim()) e.discussion = "Please add a short note";
    if (!form.consent) e.consent = "Required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, "contact_requests"), {
        ...form,
        createdAt: serverTimestamp(),
        status: "new",
        source: "landing:contact",
      });
      setSuccess(true);
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#fff" }}>

<Helmet>
  <title>Request a Demo | WorkPulse.in</title>
  <meta name="description" content="Request a WorkPulse demo for your CA firm. See entity-wise dashboards, workflow, audit trail and secure collaboration." />
  <link rel="canonical" href="https://workpulse.in/contact" />
  <meta property="og:url" content="https://workpulse.in/contact" />
</Helmet>


<Helmet>
  <title>Request a Demo | WorkPulse.in</title>

  <meta
    name="description"
    content="Request a WorkPulse demo for your CA firm. See entity-wise dashboards, workflow, activity trail and secure collaboration."
  />

  <link rel="canonical" href="https://workpulse.in/contact" />

  {/* OpenGraph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="WorkPulse.in" />
  <meta property="og:url" content="https://workpulse.in/" />
  <meta property="og:title" content="Never lose track of client work" />
  <meta
    property="og:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace. Built for Chartered Accountants in India."
  />
  <meta property="og:image" content="https://workpulse.in/og-workpulse.png" />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />

  {/* Twitter (works even without a Twitter account) */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Never lose track of client work" />
  <meta
    name="twitter:description"
    content="Entity-wise workflow, project management, timesheets and activity trail in one secure workspace."
  />
  <meta name="twitter:image" content="https://workpulse.in/og-workpulse.png" />
</Helmet>



      <Box
        sx={{
          py: { xs: 8, md: 3 },
          borderBottom: "1px solid #eef2f7",
          background:
            "radial-gradient(1200px 400px at 10% 0%, rgba(25,118,210,.08), transparent 60%), radial-gradient(1200px 400px at 90% 0%, rgba(99,102,241,.08), transparent 60%)",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
            Talk with our team
          </Typography>
          <Typography color="text.secondary">
            Fill out your info and we’ll reach out. For product support, please use in-app help.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 4 } }}>
        <Grid container spacing={4}>
          {/* Left info panel */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: "1px solid #e7ecf5" }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Quick links
                </Typography>
                <Stack spacing={1}>
                  <MuiLink component={RouterLink} to="/WorkPulsePolicy" underline="hover">
                    Security & Privacy
                  </MuiLink>
                  <MuiLink component={RouterLink} to="/user-task-summary" underline="hover">
                    User & Task Summary
                  </MuiLink>
                  <MuiLink component={RouterLink} to="/timesheet-insights" underline="hover">
                    Timesheet Insights
                  </MuiLink>
                </Stack>
              </Paper>

              <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e7ecf5" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Teams scale faster with WorkPulse.in
                  </Typography>
                  <Typography color="text.secondary">
                    “We replaced spreadsheets with one shared workspace.
                    Setup was quick and visibility improved across entities and projects.”
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right form */}
          <Grid item xs={12} md={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: "1px solid #e7ecf5",
                boxShadow: "0 8px 24px rgba(16,24,40,.06)",
              }}
            >
              <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      value={form.firstName}
                      onChange={handleChange("firstName")}
                      fullWidth
                      required
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      value={form.lastName}
                      onChange={handleChange("lastName")}
                      fullWidth
                      required
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Company Email"
                      value={form.companyEmail}
                      onChange={handleChange("companyEmail")}
                      type="email"
                      fullWidth
                      required
                      error={!!errors.companyEmail}
                      helperText={errors.companyEmail}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      fullWidth
                      inputProps={{ inputMode: "tel" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name"
                      value={form.companyName}
                      onChange={handleChange("companyName")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Company Size"
                      value={form.companySize}
                      onChange={handleChange("companySize")}
                      fullWidth
                      required
                      error={!!errors.companySize}
                      helperText={errors.companySize}
                    >
                      {COMPANY_SIZES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Country"
                      value={form.country}
                      onChange={handleChange("country")}
                      fullWidth
                      required
                      error={!!errors.country}
                      helperText={errors.country}
                    >
                      {COUNTRIES.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="What would you like to discuss?"
                      value={form.discussion}
                      onChange={handleChange("discussion")}
                      fullWidth
                      required
                      error={!!errors.discussion}
                      helperText={errors.discussion}
                      multiline
                      minRows={4}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.consent}
                          onChange={handleChange("consent")}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree that WorkPulse may contact me about this inquiry and
                          I acknowledge the{" "}
                          <MuiLink component={RouterLink} to="/WorkPulsePolicy" underline="hover">
                            Privacy Policy
                          </MuiLink>.
                        </Typography>
                      }
                    />
                    {errors.consent && (
                      <Typography variant="caption" color="error">
                        {errors.consent}
                      </Typography>
                    )}
                  </Grid>

                  {submitError && (
                    <Grid item xs={12}>
                      <Alert severity="error">{submitError}</Alert>
                    </Grid>
                  )}
                  {success && (
                    <Grid item xs={12}>
                      <Alert severity="success">
                        Thanks! We’ve received your details. Our team will get in touch shortly.
                      </Alert>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={submitting}
                        startIcon={
                          submitting ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : null
                        }
                      >
                        {submitting ? "Submitting…" : "Submit"}
                      </Button>
                      <Button
                        type="button"
                        variant="text"
                        onClick={() => {
                          setForm(initialForm);
                          setErrors({});
                          setSuccess(false);
                          setSubmitError("");
                        }}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
